// ============================================
// GLOBAL ERROR HANDLER
// ============================================
// Merkezi hata yönetimi - tüm API hatalarını yakalar
// ============================================

import toast from 'react-hot-toast';
import i18next from 'i18next';

/**
 * API hatasından kullanıcı dostu mesaj çıkarır
 */
export function getErrorMessage(error: any, defaultMessage?: string): string {
  const t = (key: string) => i18next.t(key);
  const status = error?.response?.status || error?.status;
  const serverMessage = error?.response?.data?.message || error?.data?.message || error?.message;

  // Status code bazlı mesajlar
  const statusMessages: Record<number, string> = {
    400: t('common:errors.http.400'),
    401: t('common:errors.http.401'),
    402: t('common:errors.http.402'),
    403: t('common:errors.http.403'),
    404: t('common:errors.http.404'),
    409: t('common:errors.http.409'),
    413: t('common:errors.http.413'),
    422: t('common:errors.http.422'),
    429: t('common:errors.http.429'),
    500: t('common:errors.http.500'),
    502: t('common:errors.http.502'),
    503: t('common:errors.http.503'),
  };

  // Bilinen sunucu mesajları → i18n key mapping
  const knownMessages: Record<string, string> = {
    // Auth
    'Invalid credentials': t('common:errors.server.invalidCredentials'),
    'Invalid email or password': t('common:errors.server.invalidCredentials'),
    'Email already exists': t('common:errors.server.emailExists'),
    'Email already registered': t('common:errors.server.emailExists'),
    'User not found': t('common:errors.server.userNotFound'),
    'Account disabled': t('common:errors.server.accountDisabled'),
    'Invalid token': t('common:errors.server.invalidToken'),
    'Token expired': t('common:errors.server.tokenExpired'),
    'Unauthorized': t('common:errors.server.unauthorized'),
    'Forbidden': t('common:errors.server.forbidden'),

    // Jobs & Applications
    'Job not found': t('common:errors.server.jobNotFound'),
    'Job expired': t('common:errors.server.jobExpired'),
    'Job closed': t('common:errors.server.jobClosed'),
    'Already applied': t('common:errors.server.alreadyApplied'),
    'Already applied to this job': t('common:errors.server.alreadyApplied'),
    'Application not found': t('common:errors.server.applicationNotFound'),
    'Application deadline passed': t('common:errors.server.applicationDeadlinePassed'),

    // Certificates
    'Certificate not found': t('common:errors.server.certificateNotFound'),
    'Certificate already exists': t('common:errors.server.certificateExists'),
    'Certificate expired': t('common:errors.server.certificateExpired'),

    // Sea Service
    'Sea service record not found': t('common:errors.server.seaServiceNotFound'),
    'Invalid date range': t('common:errors.server.invalidDateRange'),
    'End date before start date': t('common:errors.server.endDateBeforeStart'),

    // Documents
    'Document not found': t('common:errors.server.documentNotFound'),
    'Folder not found': t('common:errors.server.folderNotFound'),
    'Document already exists': t('common:errors.server.documentExists'),

    // Company & Reviews
    'Company not found': t('common:errors.server.companyNotFound'),
    'Already reviewed': t('common:errors.server.alreadyReviewed'),
    'Already reviewed this company': t('common:errors.server.alreadyReviewed'),

    // Favorites
    'Already in favorites': t('common:errors.server.alreadyInFavorites'),
    'Not in favorites': t('common:errors.server.notInFavorites'),

    // References
    'Reference not found': t('common:errors.server.referenceNotFound'),
    'Reference already exists': t('common:errors.server.referenceExists'),

    // Profile
    'Profile not found': t('common:errors.server.profileNotFound'),

    // File uploads
    'File too large': t('common:errors.server.fileTooLarge'),
    'Invalid file type': t('common:errors.server.invalidFileType'),
    'File upload failed': t('common:errors.server.fileUploadFailed'),

    // Generic
    'Not found': t('common:errors.server.notFound'),
    'Internal server error': t('common:errors.server.internalServerError'),
    'Network error': t('common:errors.server.networkError'),
  };

  // Önce sunucu mesajını kontrol et
  if (serverMessage) {
    // Birebir eşleşme
    if (knownMessages[serverMessage]) {
      return knownMessages[serverMessage];
    }

    // Kısmi eşleşme
    const lowerMessage = serverMessage.toLowerCase();
    for (const [key, value] of Object.entries(knownMessages)) {
      if (lowerMessage.includes(key.toLowerCase())) {
        return value;
      }
    }

    // Sunucu mesajı varsa ve makul uzunluktaysa göster
    if (typeof serverMessage === 'string' && serverMessage.length < 200) {
      return serverMessage;
    }
  }

  // Status code mesajı
  if (status && statusMessages[status]) {
    return statusMessages[status];
  }

  return defaultMessage || t('common:errors.default');
}

/**
 * Login sayfasında mıyız kontrol et
 */
function isOnLoginPage(): boolean {
  if (typeof window !== 'undefined') {
    return window.location.pathname.includes('/login') || 
           window.location.pathname.includes('/register') ||
           window.location.pathname.includes('/forgot-password') ||
           window.location.pathname.includes('/reset-password');
  }
  return false;
}

/**
 * Global mutation error handler
 * QueryClient defaultOptions'ta kullanılır
 */
export function createMutationErrorHandler(
  logout: () => void,
  clearQueryCache: () => void
) {
  return (error: any) => {
    const status = error?.response?.status || error?.status;

    // Login sayfasındayken 401 hatalarını ignore et
    if (status === 401 && isOnLoginPage()) {
      return;
    }

    // 401 - Unauthorized: Oturumu sonlandır ve login'e yönlendir
    if (status === 401) {
      toast.error(i18next.t('common:errors.http.401'));
      logout();
      clearQueryCache();
      window.location.href = '/login';
      return;
    }

    // 403 - Forbidden: Yetki hatası
    if (status === 403) {
      toast.error(i18next.t('common:errors.http.403'));
      return;
    }

    // 429 - Too Many Requests: Rate limit
    if (status === 429) {
      toast.error(i18next.t('common:errors.http.429'));
      return;
    }

    // Network error
    if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
      toast.error(i18next.t('common:errors.server.networkError'));
      return;
    }

    // Diğer hatalar için genel mesaj
    const message = getErrorMessage(error);
    toast.error(message);
  };
}

/**
 * Global query error handler (opsiyonel - sadece kritik hatalar için)
 */
export function createQueryErrorHandler(
  logout: () => void,
  clearQueryCache: () => void
) {
  return (error: any) => {
    const status = error?.response?.status || error?.status;

    // Login sayfasındayken 401 hatalarını ignore et
    if (status === 401 && isOnLoginPage()) {
      return;
    }

    // Sadece 401 için logout yap ve login'e yönlendir
    if (status === 401) {
      logout();
      clearQueryCache();
      window.location.href = '/login';
    }
  };
}
